<script setup lang="ts">
import { Check, ChevronDown } from 'lucide-vue-next';
import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';

import { useFiltersStore } from '@/stores/useFiltersStore';

type Option = {
  label: string;
  value: string;
};

const props = defineProps<{
  options: Array<Option>;
  alignment?: 'left' | 'right';
}>();

const emit = defineEmits(['update:modelValue']);

const filtersStore = useFiltersStore();
const { filters } = storeToRefs(filtersStore);

const savedSelection = filters.value.sorts.length ? props.options.find(it => it.value === filters.value.sorts[0].type) : props.options[0];

const isOpen = ref(false);
const dropdown = ref();
const keyboardIndex = ref(-1);
const selectedOption = ref<Option>(savedSelection ?? props.options[0]);
const position = computed(() => props.alignment ? `${props.alignment}:0;top:110%;` : 'left:0;top:110%;');

function openDropdown() {
  if (isOpen.value)
    return;
  isOpen.value = true;
  setTimeout(() => {
    isOpen.value === true ? document.addEventListener('click', handleOutsideClick) : document.removeEventListener('click', handleOutsideClick);
    isOpen.value === true ? document.addEventListener('keydown', handleKeyboardNavigation) : document.removeEventListener('keydown', handleKeyboardNavigation);
  }, 100);
}

function closeDropdown() {
  isOpen.value = false;
  setTimeout(() => {
    isOpen.value === true ? document.addEventListener('click', handleOutsideClick) : document.removeEventListener('click', handleOutsideClick);
    isOpen.value === true ? document.addEventListener('keydown', handleKeyboardNavigation) : document.removeEventListener('keydown', handleKeyboardNavigation);
  }, 100);
}

function handleOutsideClick(event: Event) {
  const path = event.composedPath();
  const target = path[0];
  const dropdown = document.querySelector('yb-list')?.shadowRoot?.getElementById('sort_dropdown');
  // @ts-expect-error typeof target
  if (!dropdown?.contains(target)) {
    closeDropdown();
  }
};

function handleKeyboardNavigation(event: Event) {
  event.preventDefault();
  const e = event as KeyboardEvent;
  if (e.key === 'Escape' || e.key === 'Tab') {
    closeDropdown();
    return;
  }
  const options = dropdown.value.querySelectorAll('.option-row');
  if (e.key === 'Enter') {
    options?.[keyboardIndex.value].querySelector('.option')?.click();
    return;
  }
  if (e.key === 'ArrowDown') {
    keyboardIndex.value = keyboardIndex.value + 1 === props.options?.length ? 0 : keyboardIndex.value + 1;
  }
  else if (e.key === 'ArrowUp') {
    keyboardIndex.value = keyboardIndex.value - 1 < 0 ? props.options.length - 1 : keyboardIndex.value - 1;
  }
  options?.[keyboardIndex.value].focus();
}

function selectOption(option: Option) {
  selectedOption.value = option;
  isOpen.value = false;
  window.removeEventListener('click', handleOutsideClick);
  window.removeEventListener('keydown', handleKeyboardNavigation);
  emit('update:modelValue', option);
}
</script>

<template>
  <div class="custom-select" :aria-expanded="isOpen">
    <button
      :aria-haspopup="true"
      aria-controls="sort_dropdown"
      :aria-expanded="isOpen"
      role="combobox"
      class="select-button"
      @click="openDropdown"
    >
      {{ selectedOption.label }}<span class="svg" :class="{ rotate: isOpen }" aria-hidden="true"><ChevronDown
        :size="20"
        color="var(--yb-color-black)"
      /></span>
    </button>
    <ul v-show="isOpen" id="sort_dropdown" ref="dropdown" role="listbox" class="options-list" :style="position">
      <div
        v-for="(option, index) in props.options"
        :key="index"
        class="option-row"
        tabindex="0"
      >
        <div class="option-check">
          <Check v-if="option.label === selectedOption.label" :size="18" color="var(--yb-color-black)" />
        </div>
        <li
          role="option"
          :aria-selected="option.label === selectedOption.label"
          tabindex="0"
          class="option"
          @click="selectOption(option)"
          @keydown="selectOption(option)"
        >
          {{ option.label }}
        </li>
      </div>
    </ul>
  </div>
</template>

<style lang="css" scoped>
.custom-select {
  position: relative;
  width: fit-content;
  font-size: var(--yb-text-sm);
  font-weight: var(--yb-text-light);
}

.select-button {
  display: flex;
  align-items: center;
  width: 100%;
  padding: var(--yb-spacing-xs) var(--yb-spacing-sm);
  background-color: var(--yb-color-white);
  color: var(--yb-color-black);
  border: 1px solid var(--yb-color-mediumgray);
  border-radius: var(--yb-radius-sm);
  outline-color: var(--yb-outline);
  cursor: pointer;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;

  .svg {
    position: relative;
    top: 2px;
    margin-left: var(--yb-spacing-xs);

    &.rotate {
      transform: rotate(180deg);
      top: -2px;
    }

    @media (prefers-reduced-motion: no-preference) {
      transform: rotate(0);
      transition: transform 0.2s ease-out;
      transform-origin: center;
    }
  }
}

.options-list {
  position: absolute;
  list-style-type: none;
  padding: var(--yb-spacing-xs);
  background-color: var(--yb-color-white);
  border-radius: var(--yb-radius-sm);
  box-shadow: var(--yb-color-mediumgray) 0 4px 8px 0;
  z-index: 10;

  @media (prefers-reduced-motion: no-preference) {
    opacity: 0;
    transform: translateY(-10px);
    animation: opening 0.2s ease-out forwards;
  }
}

.option-row {
  display: grid;
  grid-template-columns: 30px 1fr;
  place-items: baseline;
  border-radius: var(--yb-radius-sm);
  outline: none;

  &:hover {
    background-color: var(--yb-color-lightgray);
  }

  &:focus {
    background-color: var(--yb-color-lightgray);
  }

  &:focus-visible {
    background-color: var(--yb-color-lightgray);
  }

  &:focus-within {
    background-color: var(--yb-color-lightgray);
  }
}

.option-check {
  position: relative;
  top: 4px;
  justify-self: center;
}

.option {
  width: 100%;
  padding-block: var(--yb-spacing-xs);
  padding-right: var(--yb-spacing-sm);
  color: var(--yb-color-black);
  text-wrap: nowrap;
  outline: none;
  cursor: pointer;
}

@keyframes opening {
  to {
    opacity: 1;
    transform: translateY(0px);
  }
}
</style>
